import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { PasswordInput } from '../components/PasswordInput';
import { adminApi, adminAuth, type AdminSettings } from '../lib/adminApi';

/**
 * Admin Settings page. Two sections:
 *   1. System overview – read-only view of env-derived runtime config
 *      (price, support email addresses, Companies House mode). Lets the
 *      operator confirm what the running service is configured with.
 *   2. Change admin password – verifies the current password server-side,
 *      stores a new scrypt hash in the `settings` table that overrides the
 *      env-based password. New password takes effect on next sign-in.
 */
export function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Change-password form state.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSavedMsg, setPwSavedMsg] = useState<string | null>(null);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    adminApi
      .getSettings()
      .then(setSettings)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load settings'))
      .finally(() => setLoading(false));
  }, [navigate]);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwSavedMsg(null);
    if (newPassword.length < 10) {
      setPwError('New password must be at least 10 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await adminApi.changePassword({ currentPassword, newPassword, confirmPassword });
      setPwSavedMsg('Password updated. Use the new password next time you sign in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Refresh the source indicator from "environment" → "database".
      const fresh = await adminApi.getSettings();
      setSettings(fresh);
    } catch (e) {
      setPwError(e instanceof Error ? e.message : 'Could not change password');
    } finally {
      setPwSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-sm text-slate-500">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
      <p className="mt-1 text-sm text-slate-500">
        System configuration and admin account.
      </p>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {settings && (
        <>
          {/* ── Warning banner if still on env-default password ─────── */}
          {settings.adminPassword.usingDefault && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-semibold">Using default admin password</div>
              <div className="mt-1">
                The admin password is currently sourced from the environment file with its dev
                default. Change it below before going live.
              </div>
            </div>
          )}

          {/* ── System overview ────────────────────────────────────── */}
          <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">System overview</h2>
            <p className="mt-1 text-xs text-slate-500">
              These values come from the backend environment. Edit{' '}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px]">
                backend/.env
              </code>{' '}
              and restart the server to change them.
            </p>

            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <Info label="Filing price">{settings.price.display} ({settings.price.pence}p)</Info>
              <Info label="Companies House mode">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    settings.companiesHouse.mode === 'LIVE'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {settings.companiesHouse.mode}
                </span>
              </Info>
              <Info label="Presenter ID">
                {settings.companiesHouse.presenterIdSet ? 'Configured' : <Missing />}
              </Info>
              <Info label="Gateway URL">
                {settings.companiesHouse.gatewayUrlSet ? 'Configured' : <Missing />}
              </Info>
              <Info label="Sender address">{settings.support.fromEmail || <Missing />}</Info>
              <Info label="Admin notify email">{settings.support.notifyEmail || <Missing />}</Info>
            </dl>
          </section>

          {/* ── Change admin password ──────────────────────────────── */}
          <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Change admin password</h2>
            <p className="mt-1 text-xs text-slate-500">
              Current source:{' '}
              <span className="font-mono">{settings.adminPassword.source}</span>.
              {settings.adminPassword.source === 'environment'
                ? ' Once you set a new password it will be stored in the database and override the .env value.'
                : ' Stored as a salted scrypt hash in the database.'}
            </p>

            <form onSubmit={changePassword} className="mt-4 max-w-md space-y-4">
              <Field label="Current password">
                <PasswordInput
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
              <Field label="New password" hint="At least 10 characters">
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field label="Confirm new password">
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>

              {pwError && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{pwError}</div>
              )}
              {pwSavedMsg && (
                <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{pwSavedMsg}</div>
              )}

              <button type="submit" disabled={pwSaving} className="btn-primary">
                {pwSaving ? 'Saving…' : 'Update password'}
              </button>
            </form>
          </section>

          <TwoFactorSection />
        </>
      )}
    </AdminLayout>
  );
}

/**
 * Two-factor auth management. Three states:
 *  - disabled → button to start setup
 *  - setup in progress → show QR code + input for verification code
 *  - enabled → warning + "disable" flow that needs password + current code
 */
function TwoFactorSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<{
    secret: string;
    qrCodeDataUrl: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  // Disable form state.
  const [disPassword, setDisPassword] = useState('');
  const [disCode, setDisCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  useEffect(() => {
    adminApi.twoFactorStatus().then((s) => setEnabled(s.enabled)).catch(() => setEnabled(false));
  }, []);

  async function startSetup() {
    setBusy(true);
    setMsg(null);
    try {
      const s = await adminApi.twoFactorSetup();
      setSetup({ secret: s.secret, qrCodeDataUrl: s.qrCodeDataUrl });
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Setup failed' });
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable() {
    if (!setup) return;
    if (!/^\d{6}$/.test(code)) {
      setMsg({ tone: 'err', text: 'Enter the 6-digit code from your authenticator app.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await adminApi.twoFactorEnable({ secret: setup.secret, code });
      setEnabled(true);
      setSetup(null);
      setCode('');
      setMsg({
        tone: 'ok',
        text: '2FA is now enabled. You will be asked for a code every time you sign in.',
      });
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Could not verify code' });
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisable() {
    if (!/^\d{6}$/.test(disCode)) {
      setMsg({ tone: 'err', text: 'Enter the 6-digit code from your authenticator app.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await adminApi.twoFactorDisable({ password: disPassword, code: disCode });
      setEnabled(false);
      setShowDisable(false);
      setDisPassword('');
      setDisCode('');
      setMsg({ tone: 'ok', text: '2FA disabled.' });
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Could not disable 2FA' });
    } finally {
      setBusy(false);
    }
  }

  if (enabled === null) {
    return (
      <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <p className="text-sm text-slate-500">Loading two-factor status…</p>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Two-factor authentication</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
            enabled
              ? 'bg-green-100 text-green-700'
              : 'bg-amber-100 text-amber-700'
          }`}
        >
          {enabled ? 'ON' : 'OFF'}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Adds a 6-digit code from your authenticator app on top of your admin
        password. Recommended — without 2FA, a leaked password is enough for
        anyone to sign in.
      </p>

      {msg && (
        <div
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            msg.tone === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {msg.text}
        </div>
      )}

      {enabled ? (
        <div className="mt-4">
          {!showDisable ? (
            <button
              onClick={() => setShowDisable(true)}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Disable 2FA
            </button>
          ) : (
            <div className="rounded-lg bg-slate-50 p-4">
              <p className="text-sm text-slate-700">
                To disable 2FA, confirm your admin password and a current 6-digit code.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <PasswordInput
                  placeholder="Admin password"
                  value={disPassword}
                  onChange={(e) => setDisPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={disCode}
                  onChange={(e) => setDisCode(e.target.value.replace(/\D/g, ''))}
                  className="input text-center tracking-[0.4em]"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={confirmDisable}
                  disabled={busy}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:bg-slate-300"
                >
                  {busy ? 'Disabling…' : 'Disable 2FA'}
                </button>
                <button
                  onClick={() => setShowDisable(false)}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : setup ? (
        <div className="mt-4 rounded-lg bg-slate-50 p-4">
          <p className="text-sm text-slate-700">
            Scan this QR code with your authenticator app (Google Authenticator,
            1Password, Bitwarden, Authy, etc.).
          </p>
          <div className="mt-3 flex flex-wrap items-start gap-4">
            <img
              src={setup.qrCodeDataUrl}
              alt="2FA QR code"
              className="h-40 w-40 rounded bg-white p-2 ring-1 ring-slate-200"
            />
            <div className="flex-1 text-xs">
              <div className="text-slate-500">Or enter this secret manually:</div>
              <code className="mt-1 block break-all rounded bg-white px-2 py-1 font-mono text-[11px] ring-1 ring-slate-200">
                {setup.secret}
              </code>
              <p className="mt-3 text-slate-500">
                After adding the account, enter the current 6-digit code below to
                complete setup.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="input max-w-[160px] text-center text-lg tracking-[0.4em]"
              autoComplete="one-time-code"
              autoFocus
            />
            <button
              onClick={confirmEnable}
              disabled={busy}
              className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:bg-slate-300"
            >
              {busy ? 'Verifying…' : 'Verify and enable'}
            </button>
            <button
              onClick={() => {
                setSetup(null);
                setCode('');
                setMsg(null);
              }}
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={startSetup}
          disabled={busy}
          className="mt-4 rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:bg-slate-300"
        >
          {busy ? 'Preparing…' : 'Enable 2FA'}
        </button>
      )}
    </section>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function Missing() {
  return <span className="text-xs text-amber-700">Not configured</span>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="block text-sm font-medium text-slate-700">{label}</span>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
